/**
 * SortableList — 统一的排序列表（拖拽 + 上移 / 下移）
 *
 * 全仓「调整顺序」交互的唯一实现：每行 = 拖拽把手 + 位次 + 内容 + 上移 / 下移 + 行尾操作（下架 / 移除…）。
 * 版式取自支付商品排序（`ProductOrderDialog`）：圆角容器、行间分隔线、拖拽时目标行上沿出强调色插入线、
 * 被拖行压淡、拖到末位用底部「放到末尾」落点。
 *
 * - **受控**：本组件不存状态，`items` 进、`onReorder(next)` 出；保存时机（草稿 / 立即落库）由调用方定。
 * - **拖拽只是鼠标快捷方式**：HTML5 DnD 对键盘与触屏都不友好，所以上移 / 下移按钮恒在。
 * - **部分项可排序**：`isSortable` 为假的行不显示把手与上下移、不可拖、不占位次（显示 `—`），
 *   它们的位置在重排时原位不动（`~/lib/reorder` 的 `refillSortableSlots`）。约定可排序项排在前面。
 * - **RBAC**：只读 / 保存中传 `disabled`，把手、拖拽与按钮一并失效；起拖、落点、按钮都在入口处判，
 *   不靠 `draggable` 属性（子元素如 `<img>` 的原生拖拽会冒泡进来）。
 */

import { type ReactNode, useState } from 'react';
import { Icons } from '~/lib/icons';
import { moveItem, moveItemBefore, refillSortableSlots } from '~/lib/reorder';

const END_DROP_KEY = '__end__';

export function SortableList<T>({
  items,
  getKey,
  itemLabel,
  onReorder,
  renderContent,
  renderActions,
  isSortable = () => true,
  disabled = false,
  endDropLabel = '放到末尾',
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  /** 用于上移 / 下移按钮的无障碍名称与 title（`上移 ${label}`） */
  itemLabel: (item: T) => string;
  onReorder: (next: T[]) => void;
  /** 行主体（名称 / 副标题 / 徽标…）；占满剩余宽度 */
  renderContent: (item: T, position: number | null) => ReactNode;
  /** 行尾操作按钮（下架 / 移除 / 上架…） */
  renderActions?: (item: T) => ReactNode;
  /** 该行是否有位次、可拖可上下移；缺省全部可排序 */
  isSortable?: (item: T) => boolean;
  disabled?: boolean;
  endDropLabel?: string;
}) {
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const sortable = items.filter(isSortable);
  const byKey = new Map(items.map((item) => [getKey(item), item]));

  const commit = (nextSortable: T[] | null) => {
    if (nextSortable === null || disabled) return;
    onReorder(refillSortableSlots(items, isSortable, nextSortable));
  };
  const moveBy = (item: T, delta: -1 | 1) => {
    const from = sortable.indexOf(item);
    commit(moveItem(sortable, from, from + delta));
  };
  const endDrag = () => {
    setDraggingKey(null);
    setDropKey(null);
  };
  const dragging = draggingKey === null ? undefined : byKey.get(draggingKey);
  const draggingSortable = dragging !== undefined && isSortable(dragging);

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-elev)',
        overflow: 'hidden',
      }}
    >
      {items.map((item, index) => {
        const key = getKey(item);
        const rowSortable = isSortable(item);
        const sortIndex = rowSortable ? sortable.indexOf(item) : -1;
        const label = itemLabel(item);
        return (
          <li
            key={key}
            className="hstack"
            draggable={rowSortable && !disabled}
            onDragStart={(e) => {
              if (!rowSortable || disabled) {
                e.preventDefault();
                return;
              }
              setDraggingKey(key);
              e.dataTransfer.effectAllowed = 'move';
              // Firefox 不设 data 就不触发 drag 事件序列
              e.dataTransfer.setData('text/plain', key);
            }}
            onDragEnd={endDrag}
            onDragOver={(e) => {
              // 只收「正拖着的可排序项 → 另一个可排序行」；不 preventDefault = 浏览器显示禁止光标
              if (disabled || !draggingSortable || !rowSortable || draggingKey === key) {
                setDropKey(null);
                return;
              }
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDropKey(key);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragging !== undefined && draggingSortable && rowSortable) {
                commit(moveItemBefore(sortable, dragging, item));
              }
              endDrag();
            }}
            style={{
              gap: 'var(--gap)',
              padding: 'calc(var(--gap) * 0.7) var(--gap)',
              borderTop: index === 0 ? undefined : '1px solid var(--border)',
              // 拖拽指示线：目标行上沿加粗成强调色，表示「放在它之前」
              boxShadow:
                dropKey === key && draggingKey !== key
                  ? 'inset 0 2px 0 0 var(--accent)'
                  : undefined,
              opacity: draggingKey === key ? 0.45 : undefined,
              cursor: rowSortable && !disabled ? 'grab' : undefined,
            }}
          >
            {rowSortable ? (
              <span
                className="muted"
                aria-hidden="true"
                title="拖拽调整顺序"
                style={{ lineHeight: 1 }}
              >
                <Icons.IconGrip size={12} />
              </span>
            ) : null}
            <span
              className="muted mono"
              style={{ width: 20, textAlign: 'right', fontSize: 'var(--text-xs)' }}
            >
              {rowSortable ? sortIndex + 1 : '—'}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {renderContent(item, rowSortable ? sortIndex + 1 : null)}
            </div>
            {rowSortable ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-icon"
                  title={`上移 ${label}`}
                  aria-label={`上移 ${label}`}
                  disabled={disabled || sortIndex === 0}
                  onClick={() => moveBy(item, -1)}
                >
                  <Icons.IconChevUp size={11} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon"
                  title={`下移 ${label}`}
                  aria-label={`下移 ${label}`}
                  disabled={disabled || sortIndex === sortable.length - 1}
                  onClick={() => moveBy(item, 1)}
                >
                  <Icons.IconChevDown size={11} />
                </button>
              </>
            ) : null}
            {renderActions?.(item)}
          </li>
        );
      })}
      {/* 末位放置区：拖到最后一项之后需要一个落点，否则只能「放在某项之前」，最后一个位置永远够不着 */}
      {draggingSortable && sortable.length > 1 ? (
        <li
          onDragOver={(e) => {
            if (disabled) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropKey(END_DROP_KEY);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragging !== undefined) commit(moveItemBefore(sortable, dragging, null));
            endDrag();
          }}
          className="muted"
          style={{
            padding: 'calc(var(--gap) * 0.7) var(--gap)',
            fontSize: 'var(--text-sm)',
            textAlign: 'center',
            borderTop: '1px dashed var(--border)',
            background: dropKey === END_DROP_KEY ? 'var(--bg-subtle)' : undefined,
          }}
        >
          {endDropLabel}
        </li>
      ) : null}
    </ul>
  );
}
